import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, UserCheck, Database, Scale } from 'lucide-react';

export default function Terms() {
  const sections = [
    {
      title: "1. Services",
      content: "Trademarking.ai provides automated trademark search and analysis. Results are preliminary and not a substitute for legal advice.",
      icon: Shield
    },
    {
      title: "2. User Responsibilities",
      content: "Users must provide accurate information and understand results are not guaranteed. Final trademark decisions remain the user's responsibility.",
      icon: UserCheck
    },
    {
      title: "3. Disclaimer of Warranties",
      content: "Services provided as is, without warranties. We don't guarantee accuracy, completeness, or reliability of results.",
      icon: AlertTriangle
    },
    {
      title: "4. Limitation of Liability",
      content: "Trademarking.ai isn't liable for decisions made based on our reports. Users assume all risks associated with using our services.",
      icon: Scale
    },
    {
      title: "5. Data Usage",
      content: "We collect and store submitted information to provide services and improve functionality. See Privacy Policy for details.",
      icon: Database
    }
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-b from-white to-gray-50">
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium animate-float">
                Powered by AI
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Terms of Service
            </h1>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-3xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.2)] p-6 md:p-12 mb-12">
            {/* Important Notice */}
            <div className="bg-yellow-50 rounded-[16px] p-8 mb-12">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-yellow-800 mb-3">Important Notice:</h2>
                  <p className="text-yellow-900 text-lg">
                    Trademarking.ai is not a law firm and does not provide legal services. The information provided is for research purposes only. 
                    We strongly recommend consulting with a qualified trademark attorney before making any decisions about your trademark rights. 
                    Users proceed at their own risk.
                  </p>
                </div>
              </div>
            </div>

            {/* Terms Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => (
                <div key={index} className="p-6 rounded-[16px] border border-gray-100 hover:border-gray-200 transition-all duration-300 bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      <section.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold mb-3 text-gray-900">{section.title}</h2>
                      <p className="text-gray-600 text-lg">{section.content}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-12 text-center">
                <Link 
                  to="/" 
                  className="inline-flex items-center justify-center px-6 py-3 text-lg font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-8">
            <div className="flex flex-col md:flex-row gap-4 md:gap-8">
              <Link to="/faq" className="text-gray-600 hover:text-gray-900">FAQ</Link>
              <Link to="/about" className="text-gray-600 hover:text-gray-900">About Trademarking.ai</Link>
              <Link to="/terms" className="text-gray-600 hover:text-gray-900">Terms of Service</Link>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-lg text-gray-600 font-medium mb-4">
              <strong>COMING SOON:</strong> Generative domain and trademark names, optimized for risk and branding power
            </p>
            <div className="text-sm text-gray-500">
              Copyright © 2025 Sharpe Idea Studio
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}